import type {
  ProductPageCampaignFragment,
  RootLayoutCampaignFragment,
} from "@server/gql/graphql";
import type { Product, Variant } from "types";
import z from "zod";

const ruleValueSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
});

const ruleSchema = z.object({
  id: z.string(),
  dimension: z.enum(["brand", "category", "designer", "flag", "series"]),
  operator: z.enum(["include", "exclude"]),
  values: z.array(ruleValueSchema),
});

const rulesSchema = z.array(ruleSchema);

function codesOf(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string => typeof item === "string" && item.length > 0,
    );
  }
  if (typeof value === "string" && value.length > 0) return [value];
  return [];
}

function brandCodeOf(product: Product | Variant): string {
  const { brandCode } = product;
  if (typeof brandCode === "string") return brandCode;
  return codesOf(brandCode)[0] ?? "";
}

export const getMatchingCampaigns = ({
  campaigns,
  product,
  flags: flagOverride,
}: {
  campaigns:
    | (
        | ProductPageCampaignFragment
        | RootLayoutCampaignFragment
        | null
        | undefined
      )[]
    | null;
  product: Product | Variant;
  /** PDP keeps the old `variant.flags ?? product.flags` list. Listings omit this and use `product.flags`. */
  flags?: string[];
}) => {
  const flags = flagOverride ?? product.flags ?? [];
  const brandCode = brandCodeOf(product);
  const designerCodes = codesOf(product.designerCode);
  const categoryCodes = codesOf(product.totalCategoryCodes);

  if (!campaigns?.length) {
    return undefined;
  }

  return campaigns.filter((campaign) => {
    if (!campaign) return false;
    if (campaign.rules) {
      const { data: rules, success } = rulesSchema.safeParse(campaign.rules);

      if (success && rules.length > 0) {
        return rules.every((rule) => {
          if (rule.dimension === "flag") {
            return rule.operator === "include"
              ? rule.values.some((value) => flags.includes(value.code))
              : rule.values.every((value) => !flags.includes(value.code));
          }
          if (rule.dimension === "brand") {
            return rule.operator === "include"
              ? rule.values.some((value) => brandCode === value.code)
              : rule.values.every((value) => brandCode !== value.code);
          }
          if (rule.dimension === "designer") {
            return rule.operator === "include"
              ? rule.values.some((value) => designerCodes.includes(value.code))
              : rule.values.every(
                  (value) => !designerCodes.includes(value.code),
                );
          }
          if (rule.dimension === "category") {
            return rule.operator === "include"
              ? rule.values.some((value) => categoryCodes.includes(value.code))
              : rule.values.every(
                  (value) => !categoryCodes.includes(value.code),
                );
          }
          if (rule.dimension === "series") {
            return rule.operator === "include"
              ? rule.values.some((value) => product.series === value.name)
              : rule.values.every((value) => product.series !== value.name);
          }
          return false;
        });
      }
    }
    return Boolean(campaign.commerceId && flags.includes(campaign.commerceId));
  });
};
